import { IndicationStatus, type PricedQuoteCard } from '@/components/quotes/quoteCardTypes'

interface PricedFilterOption {
  value: string
  label: string
  test: (quote: PricedQuoteCard) => boolean
}

export interface PricedFilter {
  key: string
  label: string
  options: PricedFilterOption[]
}

const COVERAGE_PRICES: PricedFilter = {
  key: 'coverage-prices',
  label: 'Coverage prices',
  options: [
    {
      value: 'per-coverage',
      label: 'Per coverage',
      test: (quote) => quote.coverages.every((coverage) => coverage.premium !== null),
    },
    {
      value: 'total-only',
      label: 'Total only',
      test: (quote) => quote.coverages.some((coverage) => coverage.premium === null),
    },
  ],
}

const PLACED_THROUGH: PricedFilter = {
  key: 'placed-through',
  label: 'Placed through',
  options: [
    { value: 'direct', label: 'Direct', test: (quote) => !quote.sourcedThrough },
    { value: 'wholesaler', label: 'Wholesaler', test: (quote) => !!quote.sourcedThrough },
  ],
}

const DETAILS: PricedFilterOption[] = [
  { value: 'fees', label: 'Taxes & fees', test: (quote) => !!quote.oneTimeFees?.length },
  {
    value: 'comprehensive',
    label: 'Comprehensive deductible',
    test: (quote) => quote.coverages.some((coverage) => coverage.comprehensiveDeductible != null),
  },
  {
    value: 'documents',
    label: 'Outstanding documents',
    test: (quote) => !!quote.outstandingDocuments?.length,
  },
  { value: 'current-insurer', label: 'Current insurer', test: (quote) => !!quote.isCurrentInsurer },
  { value: 'grouped', label: 'Grouped', test: (quote) => (quote.memberIds?.length ?? 0) > 1 },
  { value: 'program', label: 'Named program', test: (quote) => !!quote.carrierProgram },
]

/** Every trait the production mapper can vary on a priced indication. */
export const INDICATION_FILTERS: PricedFilter[] = [
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'none', label: 'No status', test: (quote) => !quote.indicationStatus },
      {
        value: 'reviewing',
        label: 'Reviewing submitted info',
        test: (quote) => quote.indicationStatus === IndicationStatus.REVIEWING_SUBMITTED_INFO,
      },
      {
        value: 'attestation',
        label: 'Needs attestation',
        test: (quote) => quote.indicationStatus === IndicationStatus.NEEDS_ATTESTATION,
      },
    ],
  },
  {
    key: 'price',
    label: 'Price',
    options: [
      { value: 'carrier', label: "Carrier's price", test: (quote) => !quote.isEstimateOnly },
      {
        value: 'estimate',
        label: 'Our estimate',
        test: (quote) => !!quote.isEstimateOnly && !quote.totalPremiumRange,
      },
      { value: 'range', label: 'Our estimate, price range', test: (quote) => !!quote.totalPremiumRange },
    ],
  },
  COVERAGE_PRICES,
  PLACED_THROUGH,
  { key: 'details', label: 'Details', options: DETAILS },
]

/** Every trait the production mapper can vary on a firm quote; status and estimates are indication-only. */
export const QUOTED_FILTERS: PricedFilter[] = [
  {
    key: 'round',
    label: 'Round',
    options: [
      { value: 'current', label: 'Current', test: (quote) => !quote.supersededAt },
      { value: 'superseded', label: 'Superseded', test: (quote) => !!quote.supersededAt },
    ],
  },
  {
    key: 'checkout',
    label: 'Checkout',
    options: [
      { value: 'bundled', label: 'Bundled', test: (quote) => !quote.itemizedCheckoutStyle },
      {
        value: 'separately',
        label: 'Coverages sold separately',
        test: (quote) => !!quote.itemizedCheckoutStyle,
      },
    ],
  },
  {
    key: 'financing',
    label: 'Financing',
    options: [
      { value: 'plans', label: 'Payment plans', test: (quote) => !!quote.financing?.plans?.length },
      {
        value: 'description',
        label: 'Description only',
        test: (quote) => !quote.financing?.plans?.length && !!quote.financing?.description,
      },
      { value: 'none', label: 'No information', test: (quote) => !quote.financing },
    ],
  },
  {
    key: 'broker',
    label: 'Broker',
    options: [
      { value: 'recommended', label: 'Recommended', test: (quote) => !!quote.brokerRecommended },
      { value: 'note', label: 'Note', test: (quote) => !!quote.brokerNote },
    ],
  },
  COVERAGE_PRICES,
  PLACED_THROUGH,
  {
    key: 'details',
    label: 'Details',
    options: [
      ...DETAILS,
      { value: 'quote-document', label: 'Quote document', test: (quote) => !!quote.quoteDocuments?.length },
    ],
  },
]

export type PricedFilterSelection = Partial<Record<string, ReadonlySet<string>>>

/** Any ticked option within a filter, every filter with a tick: the quotes page's own rule. */
export function matchesPricedFilters(
  quote: PricedQuoteCard,
  filters: PricedFilter[],
  selection: PricedFilterSelection
) {
  return filters.every((filter) => {
    const ticked = selection[filter.key]
    if (!ticked?.size) return true
    return filter.options.some((option) => ticked.has(option.value) && option.test(quote))
  })
}
